<script setup>
import { ref, computed, watch, onUnmounted } from 'vue';

const props = defineProps({
  message: { type: String, default: '' },
  contactName: { type: String, default: 'João Silva' },
  contactPhone: { type: String, default: '+55 11 99999-0001' },
  inboxName: { type: String, default: 'WhatsApp Business' },
  // Legacy: array of text strings
  messages: { type: Array, default: () => [] },
  // New: array of MessageItem objects { type, content }
  sequence: { type: Array, default: () => [] },
});

// Mesmas convenções que o backend (Campaigns::MessageInterpolation) substitui,
// pra o preview ser fiel ao que o cliente recebe.
const NAME = 'João Silva';
const PHONE = '+55 11 99999-0001';
const EMAIL = 'joao@email.com';
const COMPANY = 'Empresa ABC';
const FIRST_NAME = 'João';
const sampleData = {
  nome: NAME,
  name: NAME,
  'contact.name': NAME,
  primeiro_nome: FIRST_NAME,
  first_name: FIRST_NAME,
  'contact.first_name': FIRST_NAME,
  telefone: PHONE,
  phone: PHONE,
  'contact.phone': PHONE,
  email: EMAIL,
  'contact.email': EMAIL,
  empresa: COMPANY,
  company: COMPANY,
  'contact.company': COMPANY,
  codigo_unico: 'X7K2Q9',
  unique_code: 'X7K2Q9',
};

const isTyping = ref(false);
const showMessage = ref(false);
let typingTimer = null;

const replaceVars = text => {
  if (!text) return '';
  let result = text;
  Object.entries(sampleData).forEach(([key, value]) => {
    const regex = new RegExp(
      `\\{\\{\\s*${key.replace('.', '\\.')}\\s*\\}\\}`,
      'g'
    );
    result = result.replace(regex, value);
  });
  return result;
};

const renderedMessage = computed(() => replaceVars(props.message));

const currentTime = computed(() => {
  const now = new Date();
  return now.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
});

// Typing indicator effect
watch(
  () => props.message,
  newVal => {
    if (typingTimer) clearTimeout(typingTimer);
    if (!newVal) {
      showMessage.value = false;
      isTyping.value = false;
      return;
    }
    isTyping.value = true;
    showMessage.value = false;
    typingTimer = setTimeout(() => {
      isTyping.value = false;
      showMessage.value = true;
    }, 800);
  },
  { immediate: true }
);

onUnmounted(() => {
  if (typingTimer) clearTimeout(typingTimer);
});

// Build display items from sequence or legacy messages
const getTemplatePreviewData = item => {
  const tmpl = item.content?.templateData;
  if (!tmpl?.components)
    return { type: 'text', text: item.content?.templateName || 'Template' };
  const body = tmpl.components.find(c => c.type === 'BODY');
  const footer = tmpl.components.find(c => c.type === 'FOOTER');
  const header = tmpl.components.find(c => c.type === 'HEADER');
  const btns = tmpl.components.find(c => c.type === 'BUTTONS');
  return {
    type: 'template',
    text: body?.text || '',
    footer: footer?.text || '',
    buttons: btns?.buttons || [],
    headerImageUrl:
      header?.format === 'IMAGE'
        ? item.content?.headerImageUrl ||
          header.example?.header_handle?.[0] ||
          header.example?.header_url?.[0] ||
          ''
        : '',
  };
};

const displayItems = computed(() => {
  // New sequence format: array of { type, content }
  if (props.sequence.length > 0) {
    return props.sequence
      .filter(item => item.type !== 'wait')
      .map(item => {
        if (item.type === 'template') return getTemplatePreviewData(item);
        return {
          type: item.type,
          text: replaceVars(item.content?.text || item.content?.caption || ''),
          url: item.content?.url || '',
          fileName: item.content?.fileName || '',
        };
      });
  }
  // Legacy: array of text strings
  if (props.messages.length > 0) {
    return props.messages.map(msg => ({
      type: 'text',
      text: replaceVars(msg),
    }));
  }
  // Single message
  if (renderedMessage.value) {
    return [{ type: 'text', text: renderedMessage.value }];
  }
  return [];
});

const hasContent = computed(
  () => displayItems.value.length > 0 || isTyping.value
);

// Waveform do preview de áudio: padrão FIXO (senoide com variação) — antes era
// Math.random() no template e o desenho mudava a cada re-render.
const waveformHeights = Array.from({ length: 20 }, (_, i) =>
  Math.round(10 + Math.sin(i * 1.7) * 5 + Math.sin(i * 0.6) * 3)
);
</script>

<!-- eslint-disable vue/no-static-inline-styles, vue/no-bare-strings-in-template -->
<template>
  <div class="flex flex-col items-center">
    <!-- Phone frame -->
    <div
      class="relative w-[320px] h-[580px] rounded-[2.5rem] border-[3px] border-n-slate-6 bg-n-slate-12 shadow-xl overflow-hidden"
    >
      <!-- Notch -->
      <div
        class="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-n-slate-12 rounded-b-2xl z-20"
      />

      <!-- Status bar -->
      <div
        class="flex items-center justify-between px-6 pt-2 pb-1 text-[10px] text-white z-10 relative"
        style="background-color: #075e54"
      >
        <span>9:41</span>
        <div class="flex items-center gap-1">
          <span class="i-lucide-signal w-3 h-3" />
          <span class="i-lucide-wifi w-3 h-3" />
          <span class="i-lucide-battery-full w-3 h-3" />
        </div>
      </div>

      <!-- WhatsApp header -->
      <div
        class="flex items-center gap-3 px-3 py-2 text-white"
        style="background-color: #075e54"
      >
        <span class="i-lucide-arrow-left w-5 h-5 opacity-80" />
        <div
          class="flex items-center justify-center w-9 h-9 rounded-full text-white text-xs font-bold shrink-0"
          style="background-color: #25d366"
        >
          <span class="i-lucide-bot w-5 h-5" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium truncate">{{ inboxName }}</p>
          <p class="text-[10px] opacity-70 truncate">online</p>
        </div>
        <span class="i-lucide-video w-5 h-5 opacity-80" />
        <span class="i-lucide-phone w-4 h-4 opacity-80" />
        <span class="i-lucide-ellipsis-vertical w-5 h-5 opacity-80" />
      </div>

      <!-- Chat area -->
      <div
        class="flex flex-col h-[calc(100%-120px)] overflow-y-auto"
        style="
          background-color: #e5ddd5;
          background-image: url('data:image/svg+xml,%3Csvg width=&quot;412&quot; height=&quot;412&quot; xmlns=&quot;http://www.w3.org/2000/svg&quot;%3E%3Cpath d=&quot;M0 0h412v412H0z&quot; fill=&quot;%23e5ddd5&quot;/%3E%3Cpath d=&quot;M10 10h4v4h-4zm20 0h4v4h-4zm20 0h4v4h-4zm20 0h4v4h-4z&quot; fill=&quot;%23d4cec4&quot; opacity=&quot;0.3&quot;/%3E%3C/svg%3E');
          background-size: 60px;
          background-repeat: repeat;
        "
      >
        <div class="flex-1 px-3 py-3 space-y-2">
          <!-- Empty state -->
          <div
            v-if="!hasContent"
            class="flex items-center justify-center h-full"
          >
            <div
              class="rounded-lg px-5 py-3 text-xs text-center shadow-sm"
              style="background-color: rgba(255, 255, 255, 0.8); color: #667781"
            >
              <span
                class="i-lucide-message-square-text w-10 h-10 block mx-auto mb-2 opacity-40"
              />
              <p class="font-medium">Adicione mensagens</p>
              <p class="mt-0.5 opacity-70">para visualizar aqui</p>
            </div>
          </div>

          <!-- Typing indicator -->
          <div v-if="isTyping" class="flex justify-end">
            <div
              class="relative max-w-[85%] rounded-lg rounded-tr-none px-4 py-2.5 shadow-sm"
              style="background-color: #dcf8c6"
            >
              <div
                class="absolute -right-2 top-0 w-0 h-0"
                style="
                  border-top: 8px solid #dcf8c6;
                  border-right: 8px solid transparent;
                "
              />
              <div class="flex items-center gap-1">
                <span
                  class="inline-block w-2 h-2 rounded-full animate-bounce"
                  style="background-color: #667781; animation-delay: 0ms"
                />
                <span
                  class="inline-block w-2 h-2 rounded-full animate-bounce"
                  style="background-color: #667781; animation-delay: 150ms"
                />
                <span
                  class="inline-block w-2 h-2 rounded-full animate-bounce"
                  style="background-color: #667781; animation-delay: 300ms"
                />
              </div>
            </div>
          </div>

          <!-- Message bubbles -->
          <template
            v-if="showMessage || sequence.length > 0 || messages.length > 0"
          >
            <div
              v-for="(item, idx) in displayItems"
              :key="idx"
              class="flex justify-end"
            >
              <!-- WAIT indicator (centered) -->
              <template v-if="item.type === 'wait'">
                <div class="w-full flex justify-center">
                  <div
                    class="px-3 py-1 rounded-full text-[10px]"
                    style="
                      background-color: rgba(255, 255, 255, 0.8);
                      color: #667781;
                    "
                  >
                    <span class="i-lucide-clock w-3 h-3 inline-block mr-1" />
                    Espera
                  </div>
                </div>
              </template>

              <!-- Regular message bubble -->
              <template v-else>
                <div
                  class="relative max-w-[85%] rounded-lg rounded-tr-none shadow-sm overflow-hidden"
                  style="background-color: #dcf8c6"
                >
                  <!-- Tail (first message only) -->
                  <div
                    v-if="idx === 0"
                    class="absolute -right-2 top-0 w-0 h-0"
                    style="
                      border-top: 8px solid #dcf8c6;
                      border-right: 8px solid transparent;
                    "
                  />

                  <!-- IMAGE preview -->
                  <img
                    v-if="item.type === 'image' && item.url"
                    :src="item.url"
                    class="w-full max-h-40 object-cover"
                    alt="Image"
                  />
                  <div
                    v-else-if="item.type === 'image' && !item.url"
                    class="w-full h-28 flex items-center justify-center"
                    style="background-color: #c5e1a5"
                  >
                    <span
                      class="i-lucide-image w-8 h-8"
                      style="color: #689f38"
                    />
                  </div>

                  <!-- VIDEO preview -->
                  <div
                    v-if="item.type === 'video'"
                    class="w-full h-28 flex items-center justify-center relative"
                    style="background-color: #a5d6a7"
                  >
                    <span
                      class="i-lucide-play-circle w-10 h-10 text-white drop-shadow"
                    />
                  </div>

                  <!-- AUDIO preview -->
                  <div
                    v-if="item.type === 'audio'"
                    class="px-3 py-2 flex items-center gap-2"
                  >
                    <div
                      class="w-8 h-8 rounded-full flex items-center justify-center"
                      style="background-color: #25d366"
                    >
                      <span class="i-lucide-play w-4 h-4 text-white" />
                    </div>
                    <div class="flex-1 flex items-center gap-0.5">
                      <span
                        v-for="n in 20"
                        :key="n"
                        class="w-0.5 rounded-full"
                        style="background-color: #667781"
                        :style="{
                          height: waveformHeights[n - 1] + 'px',
                        }"
                      />
                    </div>
                    <span class="text-[10px]" style="color: #667781">
                      0:30
                    </span>
                  </div>

                  <!-- DOCUMENT preview -->
                  <div
                    v-if="item.type === 'document'"
                    class="px-3 py-2 flex items-center gap-2"
                  >
                    <span
                      class="i-lucide-file-text w-8 h-8"
                      style="color: #e53935"
                    />
                    <div class="flex-1 min-w-0">
                      <p
                        class="text-xs font-medium truncate"
                        style="color: #111b21"
                      >
                        {{ item.fileName || 'documento.pdf' }}
                      </p>
                      <p class="text-[10px]" style="color: #667781">PDF</p>
                    </div>
                  </div>

                  <!-- TEMPLATE header image -->
                  <img
                    v-if="item.type === 'template' && item.headerImageUrl"
                    :src="item.headerImageUrl"
                    class="w-full max-h-40 object-cover"
                    alt="Template header"
                  />
                  <div
                    v-else-if="
                      item.type === 'template' && item.headerImageUrl === ''
                    "
                    class="w-full h-28 flex items-center justify-center"
                    style="background-color: #c5e1a5"
                  >
                    <span
                      class="i-lucide-image w-8 h-8"
                      style="color: #689f38"
                    />
                  </div>

                  <!-- Text / Caption content -->
                  <p
                    v-if="item.text"
                    class="px-3 py-2 text-[13px] whitespace-pre-wrap break-words leading-[1.35]"
                    style="color: #111b21"
                  >
                    {{ item.text }}
                  </p>

                  <!-- TEMPLATE footer -->
                  <p
                    v-if="item.type === 'template' && item.footer"
                    class="px-3 pb-1 text-[11px]"
                    style="color: #667781"
                  >
                    {{ item.footer }}
                  </p>

                  <!-- Timestamp + checks -->
                  <div
                    class="flex items-center justify-end gap-1 px-3 pb-1.5"
                    :class="{ 'pt-0.5': !item.text }"
                  >
                    <span class="text-[10px]" style="color: #667781">
                      {{ currentTime }}
                    </span>
                    <svg
                      width="16"
                      height="11"
                      viewBox="0 0 16 11"
                      class="inline-block ml-0.5"
                    >
                      <path
                        d="M11.071 0.929L4.5 7.5 1.929 4.929 0.515 6.343 4.5 10.328 12.485 2.343z"
                        fill="#53bdeb"
                      />
                      <path
                        d="M14.071 0.929L7.5 7.5 6.086 6.086 4.672 7.5 7.5 10.328 15.485 2.343z"
                        fill="#53bdeb"
                      />
                    </svg>
                  </div>

                  <!-- TEMPLATE buttons -->
                  <div
                    v-if="item.type === 'template' && item.buttons?.length > 0"
                    class="border-t"
                    style="border-color: rgba(0, 0, 0, 0.08)"
                  >
                    <div
                      v-for="(btn, bIdx) in item.buttons"
                      :key="bIdx"
                      class="text-center py-2 text-[13px] font-medium border-b last:border-b-0"
                      style="color: #53bdeb; border-color: rgba(0, 0, 0, 0.08)"
                    >
                      {{ btn.text }}
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </template>
        </div>

        <!-- Input bar -->
        <div
          class="flex items-center gap-2 px-2 py-2"
          style="background-color: #f0f2f5"
        >
          <span class="i-lucide-smile w-5 h-5" style="color: #54656f" />
          <div
            class="flex-1 rounded-full px-3 py-1.5 text-xs bg-white"
            style="color: #667781"
          >
            Mensagem
          </div>
          <div
            class="flex items-center justify-center w-8 h-8 rounded-full"
            style="background-color: #25d366"
          >
            <span class="i-lucide-mic w-4 h-4 text-white" />
          </div>
        </div>
      </div>
    </div>

    <!-- Contact info below phone -->
    <div class="mt-3 text-center">
      <p class="text-xs text-n-slate-11">
        {{ contactName }}
      </p>
      <p class="text-[10px] text-n-slate-9">
        {{ contactPhone }}
      </p>
    </div>
  </div>
</template>
